import { useTableFilter } from '@welbe/web-design-system';
import { OptionProps } from '@welbe/web-design-system/lib/components/molecules/Select/Select';
import HappyFaceSvg from 'App/assets/img/happy-face.svg?url';
import SadFaceSvg from 'App/assets/img/sad-face.svg?url';
import { AxiosResponse } from 'axios';
import { BrokersContext } from 'contexts/Brokers/Brokers';
import useBranchOfficeSelect from 'Factories/filters/hooks/useBranchOfficeSelect';
import Analytics from 'helpers/analytics';
import { useWindowSize } from 'hooks/useWindowSize';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Employee, EmployeeStatus } from 'types/employee';
import { getKey } from 'utils/getKey';
import { getJobProfiles } from 'Views/JobProfile/pages/ListJobProfile/services';
import {
	getCsvEmployee,
	getEmployees,
	postStatusEmployee,
	putAssignJogProfile,
} from './services/EmployeesServices';
import { useToast } from '@welbe/web-design-system';
import { getUserInfo } from 'utils/user';

export const useEmployees = () => {
	const { toast } = useToast();
	const emptyStateConfirmModal = {
		show: false,
		message: '',
		icon: '',
		children: '',
		onClose: () => Promise.resolve(),
	};
	const { height } = useWindowSize();
	const { riskId } = useParams() as { jobProfileType: string; riskId: string };
	const { branchOfficeOptions, selectedBranchOffice, isLoadingBranchOffice } =
		useBranchOfficeSelect();
	const navigate = useNavigate();
	const [isFilterOpen, setFilterOpen] = useState(true);
	const [isOpenAlertModal, setOpenAlertModal] = useState(false);
	const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false);
	const [selectedEmployees, setSelectedEmployees] = useState<any>([]);
	const [isOpenDrawer, setIsOpenDrawer] = useState(false);
	const [openModalMergeEmployee, setOpenModalMergeEmployee] = useState(false);
	const [showModalInactiveEmployee, setShowModalInactiveEmployee] =
		useState(false);
	const [confirmModal, setConfirmModal] = useState(emptyStateConfirmModal);
	const [isLoadingJobProfile, setLoadingJobProfile] = useState<boolean>(false);
	const [risksProfile, setRisksProfile] = useState<OptionProps[]>([]);
	const risks = riskId ? riskId.split(',') : [];
	const [selectedRiksProfile, setSelectedRiksProfile] = useState(() => {
		return [
			{
				id: '',
				value: 'Selecione..',
			},
		];
	});
	const [expositionAreas, setExpositionAreas] = useState<OptionProps[]>([]);
	const [selectedExpositionArea, setSelectedExpositionArea] = useState(() => {
		return [
			{
				id: '',
				value: 'Selecione..',
			},
		];
	});
	const [openModalAssignJobProfile, setOpenModalAssignJobProfile] =
		useState(false);
	const preventReRenderJobProfile = useRef(false);
	const { isBroker } = useContext(BrokersContext);
	const currentUser = getUserInfo();
	const {
		data,
		filters,
		loading,
		totalPages,
		currentPage,
		handleSearch,
		handleFilters,
		handleChangePage,
	} = useTableFilter({
		service: getEmployees,
		instantSearch: [
			'branchOfficeId',
			'status',
			'riskProfileIds',
			'expositionAreaIds',
		],
		tableFilters: {
			pageSize: 50,
			pageNumber: 0,
		},
	});

	const handleDownloadCsv = async () => {
		try {
			setIsLoadingAction(true);
			await getCsvEmployee(
				selectedBranchOffice.id,
				filters.name,
				filters.companyBadgeNumber,
				filters.shift,
				filters.status,
				transformArrayObjectToString(selectedEmployees),
			);
			Analytics.track('employee', 'Download CSV', {
				isBroker,
			});
			toast.success('Su CSV será enviado por correo electrónico');
			return;
		} catch (error) {
			Analytics.track('employee', 'Error Download CSV', {
				isBroker,
			});
			toast.error('Error al enviar CSV.');
		} finally {
			setIsLoadingAction(false);
		}
	};

	const transformArrayObjectToString = (arrayObject: any) => {
		return arrayObject.reduce((acc: string, item: any) => {
			if (!acc) {
				return item.id;
			}

			return `${acc},${item.id}`;
		}, '');
	};

	const handleShowModalInactiveEmployee = () => {
		setShowModalInactiveEmployee(true);
	};

	const handleCloseModalInactiveEmployee = () => {
		setShowModalInactiveEmployee(false);
	};

	const handleCloseConfirmModal = async () => {
		setConfirmModal(emptyStateConfirmModal);
		handleUnSelectEmployee();
		await handleSearch();
	};

	const handleEmployeeStatusChange = (
		status: EmployeeStatus,
		response: AxiosResponse<any, any>,
	) => {
		const messages = {
			active: {
				success: '¡Colaboradores reactivados!',
				error: 'Hubo un error, colaborador(es) no pudo ser reactivados.',
			},
			inactive: {
				success: '¡Los colaborador(es) fueron dados de baja!',
				error: 'Hubo un error, colaborador(es) no pudo ser dado de baja.',
			},
		};

		const { success, error } = messages[status];
		const { errors = [], success: successContent = [] } =
			response.data?.content || {};

		const iconModal = errors.length ? SadFaceSvg : HappyFaceSvg;

		setConfirmModal({
			show: true,
			message: errors.length ? error : successContent.length ? success : '',
			icon: iconModal,
			children: '',
			onClose: handleCloseConfirmModal,
		});
	};

	const handleSelectEmployees = (employees: any) => {
		const updatedEmployees = employees.map((employee: any) => {
			return {
				...employee,
				name: employee?.name?.props?.name || employee.name,
				status: getKey(employee.status),
			};
		});

		setSelectedEmployees(updatedEmployees);
	};

	const checkFusion = () => {
		return selectedEmployees.some(
			(employee: any) => employee.hasEmployeeMerged,
		);
	};

	const handleToggleStatusEmployee = async (status: EmployeeStatus) => {
		setIsLoadingAction(true);
		try {
			const employeesIds = selectedEmployees.map(
				(employee: Employee) => employee.id,
			);

			if (status === 'active' && checkFusion()) {
				setOpenAlertModal(true);
				return;
			}

			const response = await postStatusEmployee(employeesIds, status);

			handleEmployeeStatusChange(status, response);
		} catch (error: any) {
			Analytics.track('employee', 'Error Changing Employee Status', {
				isBroker,
				employees: selectedEmployees,
			});
			setConfirmModal({
				show: true,
				message:
					'Hubo un error, colaborador(es) no pudo ser actualizado el estado.',
				icon: SadFaceSvg,
				children: '',
				onClose: handleCloseConfirmModal,
			});
		} finally {
			setIsLoadingAction(false);
		}
	};

	const handleAssignJobProfile = async (risk: string) => {
		setIsLoadingAction(true);
		try {
			await putAssignJogProfile(selectedEmployees, risk);
			setConfirmModal({
				show: true,
				message: '¡Colaboradores vinculados!',
				icon: HappyFaceSvg,
				children: `Colaboradores vinculados: ${selectedEmployees.length}`,
				onClose: handleCloseConfirmModal,
			});
		} catch (error: any) {
			setConfirmModal({
				show: true,
				message: 'Hubo un error, por favor intenta de nuevo.',
				icon: SadFaceSvg,
				children: '',
				onClose: handleCloseConfirmModal,
			});
		} finally {
			setIsLoadingAction(false);
		}
	};

	const fetchJobProfile = async () => {
		setLoadingJobProfile(true);
		try {
			const { data } = await getJobProfiles();
			const newSelectedRiksProfile = selectedRiksProfile?.filter(
				(risk) => risk.id,
			);
			const riskProfile = data?.content?.riskProfiles.map((item: any) => {
				if (risks.includes(item.id)) {
					newSelectedRiksProfile?.push({
						id: item.id,
						value: item.name,
					});
				}
				return {
					...item,
					value: item.name,
				};
			});
			setSelectedRiksProfile(newSelectedRiksProfile);
			setRisksProfile(riskProfile);
			const newSelectedExpositionArea = selectedExpositionArea?.filter(
				(risk) => risk.id,
			);
			const expositionArea = data?.content?.expositionAreas.map((item: any) => {
				if (risks.includes(item.id)) {
					newSelectedExpositionArea?.push({
						id: item.id,
						value: item.name,
					});
				}
				return {
					...item,
					value: item.name,
				};
			});
			setSelectedExpositionArea(newSelectedExpositionArea);
			setExpositionAreas(expositionArea);
		} catch (error) {
			toast.error('Error al cargar los perfiles de riesgo');
		} finally {
			setLoadingJobProfile(false);
		}
	};

	const handleUnSelectEmployee = () => {
		setSelectedEmployees([]);
	};

	const handleEditEmployee = () => {
		if (selectedEmployees.length === 1) {
			navigate(`/employee/${selectedEmployees[0].id}`);
		}
	};

	const handleOpenModalAssignJobProfile = () => {
		setOpenModalAssignJobProfile(true);
	};

	const handleOpenModalMergeEmployee = () => {
		setOpenModalMergeEmployee(true);
	};

	const handleCloseModalAssignJobProfile = () => {
		setOpenModalAssignJobProfile(false);
	};

	const hasOnlyActive = selectedEmployees.length
		? selectedEmployees.every((employee: any) => employee.status === 'active')
		: false;

	const hasOnlyInactive = selectedEmployees.length
		? selectedEmployees.every((employee: any) => employee.status === 'inactive')
		: false;

	useEffect(() => {
		if (!preventReRenderJobProfile.current) {
			fetchJobProfile();
			preventReRenderJobProfile.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (selectedEmployees.length) {
			setIsOpenDrawer(true);
		} else {
			setIsOpenDrawer(false);
		}
	}, [selectedEmployees]);

	return {
		currentUser,
		openModalMergeEmployee,
		isLoadingBranchOffice,
		branchOfficeOptions,
		isLoadingAction,
		selectedEmployees,
		isOpenDrawer,
		showModalInactiveEmployee,
		hasOnlyActive,
		hasOnlyInactive,
		confirmModal,
		isBroker,
		isLoadingJobProfile,
		risksProfile,
		expositionAreas,
		openModalAssignJobProfile,
		isFilterOpen,
		isOpenAlertModal,
		setFilterOpen,
		setIsOpenDrawer,
		setOpenAlertModal,
		handleDownloadCsv,
		handleEditEmployee,
		handleAssignJobProfile,
		setOpenModalMergeEmployee,
		handleOpenModalMergeEmployee,
		handleToggleStatusEmployee,
		handleShowModalInactiveEmployee,
		handleCloseModalInactiveEmployee,
		handleOpenModalAssignJobProfile,
		handleCloseModalAssignJobProfile,
		height,
		data,
		loading,
		totalPages,
		currentPage,
		handleSearch,
		handleFilters,
		handleChangePage,
		handleSelectEmployees,
		filters,
	};
};
